// Phase 309 — Inline expense attachments preview surfaced directly inside
// the Expenses table row when the paperclip toggle is expanded. Lets the
// user see filename, preview (image/PDF), download, delete and replace
// without opening the Edit dialog.
//
// Props
//   expenseId    — string
//   attachments  — array of {attachment_id, original_name, content_type, size}
//   onRefresh    — () => void (re-fetch the parent expense list after mutation)
//   t            — i18n function
import React, { useRef, useState } from 'react';
import axios from 'axios';
import { getPdfBlobUrl, downloadPdf as downloadPdfUtil } from '../utils/pdfUtils';
import { toast } from 'sonner';
import { DownloadSimple, X, Eye, ArrowsCounterClockwise, FilePdf, FileImage, FileXls, FileDoc, File as FileIcon } from '@phosphor-icons/react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const formatBytes = (b) => {
  if (!b && b !== 0) return '';
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
  return `${(b / (1024 * 1024)).toFixed(2)} MB`;
};
const extOf = (n) => (n || '').toLowerCase().split('.').pop();
const isImage = (n) => ['jpg','jpeg','png','heic','heif','webp','gif'].includes(extOf(n));
const isPdf = (n) => extOf(n) === 'pdf';

const IconForFile = ({ name }) => {
  const e = extOf(name);
  if (e === 'pdf') return <FilePdf size={16} className="text-red-500" />;
  if (isImage(name)) return <FileImage size={16} className="text-blue-500" />;
  if (['xls','xlsx','csv'].includes(e)) return <FileXls size={16} className="text-emerald-600" />;
  if (['doc','docx'].includes(e)) return <FileDoc size={16} className="text-indigo-600" />;
  return <FileIcon size={16} className="text-zinc-500" />;
};

export default function ExpenseAttachmentsInline({ expenseId, attachments = [], onRefresh, t }) {
  const [previewAtt, setPreviewAtt] = useState(null); // { attachment_id, blobUrl, name, contentType }
  const [busy, setBusy] = useState(false);
  const replaceTargetRef = useRef(null);
  const addInputRef = useRef(null);
  const replaceInputRef = useRef(null);

  // Phase 735.13 — All attachment fetches routed through the shared blob
  // pipeline (`pdfUtils.getPdfBlobUrl` / `downloadPdf`) so adblockers /
  // Capacitor can never intercept the request.
  const buildAuthBlobUrl = async (attId) => {
    const url = await getPdfBlobUrl(`/expenses/${expenseId}/attachments/${attId}`, {});
    return { url, type: '' };
  };

  const handlePreview = async (att) => {
    try {
      setBusy(true);
      const { url } = await buildAuthBlobUrl(att.attachment_id);
      setPreviewAtt({ attachment_id: att.attachment_id, blobUrl: url, name: att.original_name, contentType: att.content_type });
    } catch (e) {
      toast.error(e?.message || t('errorOccurred') || 'Error');
    } finally {
      setBusy(false);
    }
  };

  const closePreview = () => {
    if (previewAtt?.blobUrl) URL.revokeObjectURL(previewAtt.blobUrl);
    setPreviewAtt(null);
  };

  const handleDownload = async (att) => {
    try {
      await downloadPdfUtil(
        `/expenses/${expenseId}/attachments/${att.attachment_id}`,
        { disposition: 'attachment' },
        att.original_name || 'attachment',
      );
    } catch (e) {
      toast.error(e?.message || t('errorOccurred') || 'Error');
    }
  };

  const handleDelete = async (att) => {
    if (!window.confirm(t('confirmDelete') || 'Delete this file?')) return;
    try {
      await axios.delete(`${API}/expenses/${expenseId}/attachments/${att.attachment_id}`);
      toast.success(t('deletedSuccess') || 'Deleted');
      onRefresh && onRefresh();
    } catch (e) {
      toast.error(t('errorOccurred') || 'Error');
    }
  };

  const handleAddFiles = async (fileList) => {
    const arr = Array.from(fileList || []);
    if (arr.length === 0) return;
    try {
      const fd = new FormData();
      arr.forEach(f => fd.append('files', f));
      await axios.post(`${API}/expenses/${expenseId}/attachments`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success(t('savedSuccess') || 'Saved');
      onRefresh && onRefresh();
    } catch (e) {
      toast.error(t('errorOccurred') || 'Error');
    }
  };

  const handleReplace = async (fileList) => {
    const file = (fileList && fileList[0]) || null;
    const target = replaceTargetRef.current;
    if (!file || !target) return;
    try {
      // Delete old then upload new
      await axios.delete(`${API}/expenses/${expenseId}/attachments/${target.attachment_id}`);
      const fd = new FormData();
      fd.append('files', file);
      await axios.post(`${API}/expenses/${expenseId}/attachments`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success(t('savedSuccess') || 'Saved');
      onRefresh && onRefresh();
    } catch (e) {
      toast.error(t('errorOccurred') || 'Error');
    } finally {
      replaceTargetRef.current = null;
    }
  };

  return (
    <div className="space-y-2" data-testid={`expense-attachments-inline-${expenseId}`}>
      <ul className="space-y-1">
        {attachments.map((a) => (
          <li
            key={a.attachment_id}
            className="flex items-center gap-2 px-2 py-1.5 border border-zinc-200 bg-white"
            data-testid={`expense-attachment-${a.attachment_id}`}
          >
            <IconForFile name={a.original_name} />
            <span className="text-xs text-zinc-700 truncate flex-1" title={a.original_name}>{a.original_name}</span>
            <span className="text-[10px] text-zinc-400 font-mono">{formatBytes(a.size)}</span>
            {(isImage(a.original_name) || isPdf(a.original_name)) && (
              <button
                type="button"
                className="text-zinc-500 hover:text-zinc-900 p-1"
                onClick={() => handlePreview(a)}
                disabled={busy}
                title={t('preview') || 'Preview'}
                data-testid={`expense-attachment-preview-${a.attachment_id}`}
              >
                <Eye size={14} />
              </button>
            )}
            <button
              type="button"
              className="text-zinc-500 hover:text-zinc-900 p-1"
              onClick={() => handleDownload(a)}
              title={t('download') || 'Download'}
              data-testid={`expense-attachment-download-${a.attachment_id}`}
            >
              <DownloadSimple size={14} />
            </button>
            <button
              type="button"
              className="text-zinc-400 hover:text-blue-600 p-1"
              onClick={() => { replaceTargetRef.current = a; replaceInputRef.current && replaceInputRef.current.click(); }}
              title={t('replace') || 'Replace'}
              data-testid={`expense-attachment-replace-${a.attachment_id}`}
            >
              <ArrowsCounterClockwise size={14} />
            </button>
            <button
              type="button"
              className="text-zinc-400 hover:text-red-600 p-1"
              onClick={() => handleDelete(a)}
              title={t('delete') || 'Delete'}
              data-testid={`expense-attachment-delete-${a.attachment_id}`}
            >
              <X size={14} />
            </button>
          </li>
        ))}
      </ul>

      <div>
        <button
          type="button"
          className="text-xs text-blue-600 hover:text-blue-800"
          onClick={() => addInputRef.current && addInputRef.current.click()}
          data-testid={`expense-attachments-add-${expenseId}`}
        >
          + {t('attachFiles') || 'Attach Files'}
        </button>
        <input
          ref={addInputRef}
          type="file"
          multiple
          accept=".pdf,.jpg,.jpeg,.png,.heic,.heif,.webp,.gif,.xls,.xlsx,.csv,.doc,.docx,.txt,application/pdf,image/*"
          className="hidden"
          onChange={(e) => { handleAddFiles(e.target.files); e.target.value = ''; }}
        />
        <input
          ref={replaceInputRef}
          type="file"
          accept=".pdf,.jpg,.jpeg,.png,.heic,.heif,.webp,.gif,.xls,.xlsx,.csv,.doc,.docx,.txt,application/pdf,image/*"
          className="hidden"
          onChange={(e) => { handleReplace(e.target.files); e.target.value = ''; }}
        />
      </div>

      {/* Preview modal */}
      {previewAtt && (
        <div
          className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4"
          onClick={closePreview}
          data-testid="expense-attachment-preview-modal"
        >
          <div
            className="bg-white max-w-4xl w-full max-h-[90vh] overflow-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-3 py-2 border-b border-zinc-200">
              <span className="text-sm font-semibold text-zinc-800 truncate" title={previewAtt.name}>{previewAtt.name}</span>
              <button
                type="button"
                className="text-zinc-500 hover:text-zinc-900"
                onClick={closePreview}
                data-testid="expense-attachment-preview-close"
              ><X size={18} /></button>
            </div>
            <div className="p-3">
              {isPdf(previewAtt.name) ? (
                <iframe
                  title={previewAtt.name}
                  src={previewAtt.blobUrl}
                  className="w-full"
                  style={{ height: '75vh', border: 0 }}
                />
              ) : (
                <img src={previewAtt.blobUrl} alt={previewAtt.name} className="max-w-full max-h-[75vh] mx-auto block" />
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
